import { useState, useEffect } from "react";
import { format } from "date-fns";
import {
  MapPin, Truck, Calendar, FileText, User, Shield, Phone, Mail,
  MessageSquare, Building2, DollarSign,
} from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { TruckRow } from "@/pages/SearchTrucksPageV2";
import { displayCompanyName } from "@/lib/displayCompanyName";

/* ─── Equipment label map ─────────────────────────────────────────── */
const eqMap: Record<string, string> = {
  van: "Van", reefer: "Reefer", flatbed: "Flatbed",
  step_deck: "Step Deck", power_only: "Power Only",
  box_truck: "Box Truck", hotshot: "Hotshot", other: "Other",
};

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

interface CompanyInfo {
  mc_number: string | null;
  dot_number: string | null;
  verification_status: string | null;
  phone: string | null;
  email: string | null;
}

interface Props {
  truck: TruckRow;
}

export function SearchTruckDetailsPanel({ truck }: Props) {
  const { profile } = useAuth();
  const isBroker = profile?.user_type === "freight_broker";

  /* ── Lazy-fetch company MC/DOT ─────────────────────────────────── */
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);

  useEffect(() => {
    if (!truck.company_id) return;
    (async () => {
      const { data } = await supabase
        .from("companies")
        .select("mc_number, dot_number, verification_status, phone, email")
        .eq("id", truck.company_id!)
        .single();
      if (data) setCompanyInfo(data as CompanyInfo);
    })();
  }, [truck.company_id]);

  const contactParts = truck.contact_info ? truck.contact_info.split(" | ") : [];
  const isVerified = !!truck.documents_verified_at;

  return (
    <div className="border-l-2 border-l-brand-teal bg-brand-teal-subtle/10 px-3 md:px-4 py-3">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">

        {/* ═══ Column 1: Truck & Equipment ═══ */}
        <div className="flex flex-col gap-3">
          <Card className="shadow-none">
            <CardHeader className="flex flex-row items-center gap-1.5 p-3 pb-0 space-y-0">
              <Truck className="h-3.5 w-3.5 text-accent" />
              <span className="text-xs font-semibold text-foreground">Truck & Equipment</span>
            </CardHeader>
            <CardContent className="p-3 pt-2 space-y-1.5">
              <Field label="Equipment" value={eqMap[truck.equipment_type] || truck.equipment_type} />
              <Field label="Load Type" value={truck.load_type || "FTL"} />
              <Field label="Length" value={truck.length_ft ? `${truck.length_ft} ft` : "—"} />
              <Field label="Max Weight" value={truck.weight_capacity ? `${truck.weight_capacity.toLocaleString()} lbs` : "—"} />
              {truck.reference_id && <Field label="Reference" value={truck.reference_id} />}
            </CardContent>
          </Card>

          <Card className="shadow-none">
            <CardHeader className="flex flex-row items-center gap-1.5 p-3 pb-0 space-y-0">
              <MapPin className="h-3.5 w-3.5 text-accent" />
              <span className="text-xs font-semibold text-foreground">Route</span>
            </CardHeader>
            <CardContent className="p-3 pt-2 space-y-1.5">
              <Field label="Origin" value={truck.origin_location || "—"} />
              <Field label="Destination" value={truck.destination_location || "🌐 Anywhere"} />
            </CardContent>
          </Card>
        </div>

        {/* ═══ Column 2: Availability & Rate ═══ */}
        <div className="flex flex-col gap-3">
          <Card className="shadow-none">
            <CardHeader className="flex flex-row items-center gap-1.5 p-3 pb-0 space-y-0">
              <Calendar className="h-3.5 w-3.5 text-accent" />
              <span className="text-xs font-semibold text-foreground">Availability</span>
            </CardHeader>
            <CardContent className="p-3 pt-2 space-y-1.5">
              <Field label="Available From" value={truck.available_from ? format(new Date(truck.available_from + "T00:00:00"), "MMM dd, yyyy") : "—"} />
              <Field label="Available To" value={truck.available_to ? format(new Date(truck.available_to + "T00:00:00"), "MMM dd, yyyy") : "—"} />
              <Field label="Posted" value={format(new Date(truck.created_at), "MMM dd, yyyy h:mm a")} />
            </CardContent>
          </Card>

          <Card className="shadow-none">
            <CardHeader className="flex flex-row items-center gap-1.5 p-3 pb-0 space-y-0">
              <DollarSign className="h-3.5 w-3.5 text-accent" />
              <span className="text-xs font-semibold text-foreground">Rate</span>
            </CardHeader>
            <CardContent className="p-3 pt-2 space-y-1.5">
              <Field label="Rate/Mile" value={truck.rate_per_mile != null ? `$${truck.rate_per_mile.toFixed(2)}` : "—"} />
            </CardContent>
          </Card>

          {truck.comments && (
            <Card className="shadow-none">
              <CardHeader className="flex flex-row items-center gap-1.5 p-3 pb-0 space-y-0">
                <FileText className="h-3.5 w-3.5 text-accent" />
                <span className="text-xs font-semibold text-foreground">Notes</span>
              </CardHeader>
              <CardContent className="p-3 pt-2">
                <p className="text-[11px] text-foreground whitespace-pre-wrap">{truck.comments}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* ═══ Column 3: Company & Contact ═══ */}
        <div className="flex flex-col gap-3">
          <Card className="shadow-none">
            <CardHeader className="flex flex-row items-center gap-1.5 p-3 pb-0 space-y-0">
              <Shield className="h-3.5 w-3.5 text-accent" />
              <span className="text-xs font-semibold text-foreground">Carrier Info</span>
            </CardHeader>
            <CardContent className="p-3 pt-2">
              <div className="flex items-center gap-2 mb-3">
                <div className="h-9 w-9 rounded-md bg-brand-teal-deep flex items-center justify-center shrink-0">
                  <span className="text-[11px] font-extrabold text-white">{getInitials(truck.company_name || "N/A")}</span>
                </div>
                <div>
                  <p className="text-xs font-bold text-foreground">{displayCompanyName(truck.company_name) || "Unknown"}</p>
                  {isVerified ? (
                    <Badge className="mt-0.5 text-[9px] px-1.5 py-0 bg-success-subtle text-success border-0 gap-0.5">
                      <Shield className="h-2.5 w-2.5" /> Verified Carrier
                    </Badge>
                  ) : (
                    <span className="mt-0.5 inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0 rounded-full bg-muted text-muted-foreground border border-border">
                      Unverified Carrier
                    </span>
                  )}
                </div>
              </div>

              <div className="space-y-1.5">
                {companyInfo?.mc_number && <Field label="MC#" value={companyInfo.mc_number} />}
                {companyInfo?.dot_number && <Field label="DOT#" value={companyInfo.dot_number} />}
                {!companyInfo && truck.company_id && (
                  <p className="text-[10px] text-muted-foreground">Loading company details…</p>
                )}
              </div>

              {contactParts.length > 0 && (
                <div className="border-t border-border pt-2 mt-3">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground mb-2">Contact</p>
                  <div className="flex items-center gap-2 mb-2">
                    <Avatar className="h-6 w-6 border border-border">
                      <AvatarFallback className="text-[9px] bg-muted">{getInitials(contactParts[0] || "?")}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-[11px] font-medium text-foreground">{contactParts[0]}</p>
                      {contactParts[1] && <p className="text-[10px] text-muted-foreground">{contactParts[1]}</p>}
                    </div>
                  </div>
                  <div className="flex gap-1.5">
                    {contactParts.some((p) => p.includes("@")) && (
                      <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1 flex-1">
                        <Mail className="h-3 w-3" /> Email
                      </Button>
                    )}
                    {contactParts.some((p) => /\d{3}/.test(p)) && (
                      <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1 flex-1">
                        <Phone className="h-3 w-3" /> Call
                      </Button>
                    )}
                    <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1 flex-1">
                      <MessageSquare className="h-3 w-3" /> Message
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ═══ Column 4: Actions ═══ */}
        <div className="flex flex-col gap-3">
          <Card className="shadow-none">
            <CardHeader className="flex flex-row items-center gap-1.5 p-3 pb-0 space-y-0">
              <DollarSign className="h-3.5 w-3.5 text-accent" />
              <span className="text-xs font-semibold text-foreground">Actions</span>
            </CardHeader>
            <CardContent className="p-3 pt-2">
              <TooltipProvider>
                <div className="flex flex-col gap-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full h-10 text-xs font-bold border-brand-teal text-brand-teal-deep hover:bg-brand-teal-subtle"
                          disabled={isBroker}
                        >
                          NEGOTIATE RATE
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {isBroker && <TooltipContent>Carrier accounts only</TooltipContent>}
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button
                          size="sm"
                          className="w-full h-10 text-xs font-bold bg-brand-teal text-white hover:bg-brand-teal/90"
                          disabled={isBroker}
                        >
                          BOOK TRUCK
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {isBroker && <TooltipContent>Carrier accounts only</TooltipContent>}
                  </Tooltip>
                </div>
              </TooltipProvider>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

/* ─── Tiny field helper ───────────────────────────────────────────── */
function Field({ label, value }: { label: string; value: string | number | null | undefined }) {
  return (
    <div className="flex justify-between text-[11px]">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-foreground">{value ?? "—"}</span>
    </div>
  );
}
